import { FEATURES } from '@constants/Text'
import { lazyLoadView } from '@utils/LazyLoad'
import { isWelcomeDismissed } from '@modules/design/utils/welcome-storage'
import { usuarioEhGestor } from '@modules/fee-fixo/utils/acesso-gestor'
import { temAcessoIaFeeFixo } from '@modules/fee-fixo/utils/acesso-ia'

// Quem tem a feature e JÁ MARCOU "não mostrar novamente" vai direto para a Visão
// Geral; os demais caem no welcome, que segue acessível pela aba "Bem-vindo".
// Nada é gravado automaticamente: `setWelcomeDismissed` só roda no `toggleDismiss`
// do checkbox — ou seja, quem nunca marcou vê a landing em toda visita a
// /fee-fixo. É o mesmo comportamento de CRM, Compliance, AAWZ Drive e FP.
//
// `require` lazy (não import no topo), mesmo padrão do CRM: evita capturar o store
// errado em teste quando o `jest.mock('@state/store')` não hoista de forma
// confiável através de um módulo importado transitivamente.
//
// ★ EXPORTADA desde 21/08/2026 (G-1 do verify iteração 2) porque o `redirect`
// da raiz resolve no `match()`, ANTES do `beforeEach` global — e portanto antes
// do `await P.getCurrentUser()` que popula `parameters/show`. Em hard-load
// (URL digitada, F5, primeira navegação pós-login) esta função decide com a
// store FRIA e manda todo mundo para o welcome. Quem conserta é
// `applyFeeFixoRedirectGuard` (`src/router/index.js`), que a chama de novo já
// com as flags carregadas — a MESMA função, não uma cópia do ramo da IA: duas
// réguas do mesmo redirect divergiriam no primeiro ajuste.
// Nome da rota da entrada em conversa ("Guia"). Constante para que quem
// precisa comparar contra ela (`tour-guiado-autostart-mixin.js`, que não
// pode deixar o tour sequestrar essa conversa — spec-008 QA G-4) não crie
// uma segunda string solta divergente desta.
export const ROTA_FEE_FIXO_GUIA = 'fee-fixo.guia'

export const resolveFeeFixoRedirect = () => {
  const store = require('@state/store').default
  const show = store.getters['parameters/show'] || {}
  // ★ ADR-003 (spec-001 do project-onboarding-with-ai-fee-fixo, 20/08/2026):
  // com a IA ligada, a ENTRADA do módulo passa a ser a conversa — antes do
  // dismiss do welcome, de propósito: quem tem o guia não volta pra Visão
  // Geral por ter marcado a caixa um dia; a entrada mudou de lugar.
  //
  // As DUAS flags, como em `main.vue` (`feeFixoIaHabilitada`): a de IA sozinha
  // não abre a entrada de um módulo que o escritório não contratou. Sem elas,
  // os dois ramos abaixo seguem byte-idênticos ao que sempre foram.
  if (show[FEATURES.FEE_FIXO] && temAcessoIaFeeFixo(show)) {
    return { name: ROTA_FEE_FIXO_GUIA }
  }
  if (show[FEATURES.FEE_FIXO] && isWelcomeDismissed('fee-fixo')) {
    return { name: 'fee-fixo.dashboard' }
  }
  return { name: 'fee-fixo.welcome' }
}

// Gate único do módulo, repetido em TODAS as rotas (pai e folhas) porque o
// vue-router 3 NÃO mescla `meta` entre pai e filho — o guard lê o registro mais
// profundo, então declarar só no pai deixaria cada folha sem gate nenhum.
//
// A landing NÃO é alcançável por quem não contratou: `can_show` estrito. A tela de
// upsell depende de uma entrada no menu que ainda não existe (hoje
// `navigation-routes.js` gateia o item em `access[FEATURES.FEE_FIXO]` puro), e um
// gate relaxado sem essa entrada só criaria um shell cujas abas de dados devolvem
// 404. Quando a entrada de upsell for criada — padrão do AAWZ Drive, com
// `can_show: access[...] || !ctx.hideCrossSell` + `noAccessIcon` —, este meta volta
// a `customPermission` JUNTO com o filtro de abas em `fee-fixo-nav-bar.vue`.
const metaFeeFixo = {
  permission: ['profissional'],
  can_show: FEATURES.FEE_FIXO,
}

export default [
  {
    path: '/fee-fixo',
    name: 'fee-fixo',
    redirect: resolveFeeFixoRedirect,
    meta: { ...metaFeeFixo },
    component: () =>
      lazyLoadView(
        import('@modules/fee-fixo/views/nav-bar/fee-fixo-nav-bar.vue')
      ),
    children: [
      {
        // Entrada em conversa do módulo (spec-001 do
        // project-onboarding-with-ai-fee-fixo). É a PRIMEIRA filha porque é a
        // primeira aba do menu (`feeFixoRoutes()`) — a ordem dos dois anda
        // junta.
        //
        // `customPermission` pelo MESMO mecanismo (e pelo mesmo motivo) de
        // `fee-fixo.roleplay.historico`: esta tela É a IA. Sem `IA - Fee
        // Fixo` não há vitrine a mostrar aqui — só um chat que o backend
        // recusa —, então o deep-link/F5 vira 404 em vez de uma superfície
        // morta. Não é `can_show` (ver o comentário do `metaFeeFixo` abaixo):
        // `customPermission` roda uma vez por navegação e não depende de a
        // view evitar chamada de API.
        path: 'guia',
        name: 'fee-fixo.guia',
        meta: {
          ...metaFeeFixo,
          customPermission: ({ show }) => temAcessoIaFeeFixo(show),
        },
        component: () =>
          lazyLoadView(
            import('@modules/fee-fixo/views/guia/view-fee-fixo-guia.vue')
          ),
      },
      {
        path: 'bem-vindo',
        name: 'fee-fixo.welcome',
        meta: { ...metaFeeFixo },
        component: () =>
          lazyLoadView(
            import('@modules/fee-fixo/views/welcome/view-fee-fixo-welcome.vue')
          ),
      },
      {
        path: 'dashboard',
        name: 'fee-fixo.dashboard',
        meta: { ...metaFeeFixo },
        component: () =>
          lazyLoadView(
            import('@modules/fee-fixo/views/view-fee-fixo-dashboard.vue')
          ),
      },
      {
        path: 'clientes',
        name: 'fee-fixo.clientes',
        meta: { ...metaFeeFixo },
        component: () =>
          lazyLoadView(
            import('@modules/fee-fixo/views/view-fee-fixo-clientes.vue')
          ),
      },
      {
        path: 'simulacao',
        name: 'fee-fixo.simulacao',
        meta: { ...metaFeeFixo },
        component: () =>
          lazyLoadView(
            import('@modules/fee-fixo/views/view-fee-fixo-simulacao.vue')
          ),
      },
      {
        path: 'simulacao/:codigoCliente',
        name: 'fee-fixo.simulacao.cliente',
        meta: { ...metaFeeFixo },
        component: () =>
          lazyLoadView(
            import('@modules/fee-fixo/views/view-fee-fixo-simulacao.vue')
          ),
      },
      {
        path: 'pipeline',
        name: 'fee-fixo.pipeline',
        meta: { ...metaFeeFixo },
        component: () =>
          lazyLoadView(
            import('@modules/fee-fixo/views/pipeline/view-fee-fixo-pipeline.vue')
          ),
      },
      {
        // Ensaio falado do discurso de migração (spec-018). Desde a 7ª
        // rodada do redesign (07/08/2026), a rota virou um SHELL (mesmo
        // padrão de `compliance.contracts`/`view-documents-navigation.vue`):
        // `redirect` pra página default + `children` reais, com
        // `BaseSegmentedTabs` no shell (`view-fee-fixo-roleplay-navigation.vue`)
        // trocando entre elas. Histórico completo em CLAUDE.md do módulo §4.
        //
        // ★ CRÍTICO — NÃO reintroduza `can_show: [FEE_FIXO, IA_FEE_FIXO]` em
        // `fee-fixo.roleplay`/`.ensaio` sem antes conferir que
        // `view-fee-fixo-roleplay.vue` segue sem chamar API nenhuma sem
        // `IA_FEE_FIXO`: é essa ausência de chamada (não a rota) que evita o
        // 403 em loop para quem tem a URL. A aba Ensaio é vitrine (mostra
        // `FeeFixoRoleplaySemAcesso`, com CTA de contato) — produto quer ela
        // sempre acessível, `can_show` a esconderia atrás de 404.
        //
        // `.historico` é EXCEÇÃO própria (11/08/2026, achado do usuário):
        // não tem valor de vitrine mostrar uma tabela vazia — usa
        // `customPermission` (guard DIFERENTE de `can_show`, mesmo mecanismo
        // de `fee-fixo.configuracoes` logo abaixo) pra vira 404 de verdade
        // no deep-link sem a flag. Não é o `can_show` que causou o loop
        // documentado acima — `customPermission` roda uma vez por navegação,
        // não depende de a view evitar chamada de API.
        //
        // Sem `:codigoCliente` em nenhum registro — o cliente em contexto é
        // 100% `feeFixo/clienteAtivo` (Vuex), o mesmo slice que a Simulação já
        // usa. `props`/`:codigoCliente?` saíram junto.
        path: 'roleplay',
        name: 'fee-fixo.roleplay',
        redirect: { name: 'fee-fixo.roleplay.ensaio' },
        meta: { ...metaFeeFixo },
        component: () =>
          lazyLoadView(
            import('@modules/fee-fixo/views/roleplay/view-fee-fixo-roleplay-navigation.vue')
          ),
        children: [
          {
            // Path próprio (não `''`) de propósito: um child path vazio
            // resolve pra `/fee-fixo/roleplay/` — com a BARRA sobrando no
            // final (vue-router concatena pai+filho com `/`) — feedback do
            // usuário. Path explícito deixa as duas filhas simétricas:
            // `/fee-fixo/roleplay/ensaio` e `/fee-fixo/roleplay/historico`.
            path: 'ensaio',
            name: 'fee-fixo.roleplay.ensaio',
            meta: { ...metaFeeFixo },
            component: () =>
              lazyLoadView(
                import('@modules/fee-fixo/views/roleplay/view-fee-fixo-roleplay.vue')
              ),
          },
          {
            path: 'historico',
            name: 'fee-fixo.roleplay.historico',
            meta: {
              ...metaFeeFixo,
              customPermission: ({ show }) => temAcessoIaFeeFixo(show),
            },
            component: () =>
              lazyLoadView(
                import('@modules/fee-fixo/views/roleplay/view-fee-fixo-roleplay-historico.vue')
              ),
          },
        ],
      },
      {
        // Premissas do escritório (spec-017). A rota é gestor-only desde
        // 06/08/2026 (pedido explícito do usuário: deep-link direto na URL
        // continuava dando acesso a não-gestor mesmo com a aba escondida no
        // menu — `customPermission` fecha esse caminho no guard global,
        // `src/router/index.js`, mesmo padrão de `solicitacoes/routes.js`
        // (`ehEscritorioOperacaoAawz`) e `finia/routes.js`). Falha vira 404,
        // igual a qualquer outro `customPermission` do repo.
        //
        // O engine NÃO depende de visitar esta rota: as premissas são
        // carregadas pelo SHELL do módulo (`fee-fixo-nav-bar.vue`, `created`,
        // ADR-017-B) assim que o usuário entra em QUALQUER tela do Fee Fixo —
        // Simulação/Clientes continuam calculando normalmente pra
        // não-gestor. Quem barra a ESCRITA continua sendo o backend
        // (`status: 403` no corpo); isto só fecha a LEITURA da tela.
        path: 'configuracoes',
        name: 'fee-fixo.configuracoes',
        meta: {
          permission: ['profissional'],
          can_show: FEATURES.FEE_FIXO,
          customPermission: ({ user }) => usuarioEhGestor(user),
        },
        component: () =>
          lazyLoadView(
            import('@modules/fee-fixo/views/view-fee-fixo-configuracoes.vue')
          ),
      },
    ],
  },
]
